import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Save, AlertCircle, Shapes } from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import type { LayoutDetail, LayoutSummary } from "../../../types/documents";

export type LayoutEditorMode = "create" | "edit";

interface LayoutEditorModalProps {
  open: boolean;
  mode: LayoutEditorMode;
  /** When editing, the layout's id (read-only in edit mode). */
  initialLayoutId?: string;
  /** Pre-populate form fields from an existing layout. */
  initial?: Partial<LayoutSummary & LayoutDetail>;
  /** Available workflow ids for the dropdown. Pass `undefined` while loading. */
  workflows?: string[];
  workflowsLoading?: boolean;
  onClose: () => void;
  onSaved?: (layoutId: string) => void;
}

interface FormState {
  layoutId: string;
  customerName: string;
  workflowId: string;
  fieldExtractionType: "rules" | "llm";
  lineitemExtractionType: "rules" | "llm";
  inputType: "" | "import" | "export";
  status: "active" | "inactive";
  certifiedStatus: boolean;
}

const EMPTY_FORM: FormState = {
  layoutId: "",
  customerName: "",
  workflowId: "",
  fieldExtractionType: "rules",
  lineitemExtractionType: "rules",
  inputType: "",
  status: "active",
  certifiedStatus: false,
};

const LAYOUT_ID_PATTERN = /^[a-zA-Z0-9_,.\-]+$/;

export function LayoutEditorModal({
  open,
  mode,
  initialLayoutId,
  initial,
  workflows,
  workflowsLoading,
  onClose,
  onSaved,
}: LayoutEditorModalProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Reset form when the modal opens for a different layout / mode.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (mode === "edit") {
      setForm({
        layoutId: initialLayoutId ?? String(initial?.layout_id ?? ""),
        customerName: String(initial?.customer_name ?? ""),
        workflowId: String(initial?.workflow_id ?? ""),
        fieldExtractionType:
          (initial as Record<string, unknown> | undefined)?.field_extraction_type ===
          "llm"
            ? "llm"
            : "rules",
        lineitemExtractionType:
          (initial as Record<string, unknown> | undefined)?.lineitem_extraction_type ===
          "llm"
            ? "llm"
            : "rules",
        inputType:
          (initial as Record<string, unknown> | undefined)?.input_type === "import"
            ? "import"
            : (initial as Record<string, unknown> | undefined)?.input_type === "export"
            ? "export"
            : "",
        status:
          (initial as Record<string, unknown> | undefined)?.status === "inactive"
            ? "inactive"
            : "active",
        certifiedStatus: Boolean(
          (initial as Record<string, unknown> | undefined)?.certified_status,
        ),
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, mode, initialLayoutId, initial]);

  const upsertMutation = useMutation({
    mutationFn: () =>
      ocrApi.upsertLayout({
        layoutId: form.layoutId.trim(),
        customerName: form.customerName.trim(),
        workflowId: form.workflowId.trim(),
        fieldExtractionType: form.fieldExtractionType,
        lineitemExtractionType: form.lineitemExtractionType,
        inputType: form.inputType,
        status: form.status,
        certifiedStatus: form.certifiedStatus,
      }),
    onSuccess: () => {
      toast.success(
        mode === "create" ? `Layout "${form.layoutId}" created` : `Layout "${form.layoutId}" updated`,
      );
      queryClient.invalidateQueries({ queryKey: ["ocr", "layouts"] });
      onSaved?.(form.layoutId);
      onClose();
    },
    onError: (err) =>
      toast.error("Save failed", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  if (!open) return null;

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.layoutId.trim()) {
      next.layoutId = "Layout ID is required";
    } else if (!LAYOUT_ID_PATTERN.test(form.layoutId.trim())) {
      next.layoutId =
        "Only letters, numbers, underscores, hyphens, dots, and commas allowed";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    upsertMutation.mutate();
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-indigo-50 p-1.5">
              <Shapes className="h-4 w-4 text-indigo-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">
              {mode === "create" ? "New layout" : `Edit layout`}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {/* Layout ID */}
          <Field
            label="Layout ID"
            required
            error={errors.layoutId}
            hint={
              mode === "create"
                ? "Lowercase, snake_case. Cannot be changed after creation."
                : "Layout ID is immutable."
            }
          >
            <input
              type="text"
              value={form.layoutId}
              onChange={(e) => setField("layoutId", e.target.value)}
              disabled={mode === "edit"}
              placeholder="e.g. nihon_pisco_co._,ltd."
              className={`w-full rounded-md border px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 disabled:bg-gray-50 disabled:text-gray-500 ${
                errors.layoutId
                  ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500"
                  : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
              }`}
            />
          </Field>

          {/* Customer */}
          <Field label="Customer" hint="Vendor or customer name (optional).">
            <input
              type="text"
              value={form.customerName}
              onChange={(e) => setField("customerName", e.target.value)}
              placeholder="e.g. john_deere"
              className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </Field>

          {/* Workflow */}
          <Field
            label="Workflow"
            hint={
              workflowsLoading
                ? "Loading workflows…"
                : workflows && workflows.length > 0
                ? "Pick a workflow this layout routes to."
                : "No workflows available — type one in."
            }
          >
            {workflows && workflows.length > 0 ? (
              <select
                value={form.workflowId}
                onChange={(e) => setField("workflowId", e.target.value)}
                className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">— Choose workflow —</option>
                {workflows.map((wf) => (
                  <option key={wf} value={wf}>
                    {wf}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={form.workflowId}
                onChange={(e) => setField("workflowId", e.target.value)}
                placeholder="e.g. AL_3"
                className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            )}
          </Field>

          {/* Extraction modes */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Field extraction">
              <RadioGroup
                value={form.fieldExtractionType}
                options={[
                  { value: "rules", label: "Rules" },
                  { value: "llm", label: "LLM" },
                ]}
                onChange={(v) => setField("fieldExtractionType", v as "rules" | "llm")}
              />
            </Field>
            <Field label="Lineitem extraction">
              <RadioGroup
                value={form.lineitemExtractionType}
                options={[
                  { value: "rules", label: "Rules" },
                  { value: "llm", label: "LLM" },
                ]}
                onChange={(v) => setField("lineitemExtractionType", v as "rules" | "llm")}
              />
            </Field>
          </div>

          {/* Input type */}
          <Field label="Input type">
            <RadioGroup
              value={form.inputType || "any"}
              options={[
                { value: "any", label: "Any" },
                { value: "import", label: "Import" },
                { value: "export", label: "Export" },
              ]}
              onChange={(v) =>
                setField("inputType", v === "any" ? "" : (v as "import" | "export"))
              }
            />
          </Field>

          {/* Status + certified */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <RadioGroup
                value={form.status}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                onChange={(v) => setField("status", v as "active" | "inactive")}
              />
            </Field>
            <Field label="Certified">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.certifiedStatus}
                  onChange={(e) => setField("certifiedStatus", e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Mark as certified
              </label>
            </Field>
          </div>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-5 py-3">
          {upsertMutation.isError ? (
            <div className="mr-auto flex items-center gap-1 text-xs text-rose-600">
              <AlertCircle className="h-3.5 w-3.5" />
              {(upsertMutation.error as Error)?.message ?? "Save failed"}
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={upsertMutation.isPending}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={upsertMutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {upsertMutation.isPending
              ? "Saving…"
              : mode === "create"
              ? "Create layout"
              : "Save changes"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-700">
        <span>
          {label}
          {required ? <span className="text-rose-500"> *</span> : null}
        </span>
        {error ? <span className="text-rose-600">{error}</span> : null}
      </label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-[11px] text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

function RadioGroup({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md bg-gray-100 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 rounded px-2 py-1 text-xs font-medium transition-colors ${
              active
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
